import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  GUIDE_VISIBILITY_HINTS,
  GUIDE_VISIBILITY_LABELS,
  GUIDE_VISIBILITIES,
  type Guide,
  type GuideVisibility,
} from "@shared/modules/guides";
import { ApiError } from "../../../core/lib/api";
import { useAuthStore } from "../../../core/stores/auth";
import { useToastStore } from "../../../core/stores/toast";
import { PageHeader } from "../../../core/components/ui/page-header";
import { Button } from "../../../core/components/ui/button";
import { Modal } from "../../../core/components/ui/modal";
import { Field, Input, Select } from "../../../core/components/ui/input";
import { Icon } from "../../../core/components/ui/icon";
import { formatDateTime } from "../../../core/lib/format";
import { guidesApi } from "../lib/api";
import { GuideStatusBadge, GuideVisibilityBadge } from "../components/badges";

/** Liste des guides : tableau complet pour l'administrateur, cartes du publié pour les autres. */
export function GuidesList() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const push = useToastStore((s) => s.push);
  const isAdmin = user?.role === "admin";

  const [guides, setGuides] = useState<Guide[] | null>(null);
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [visibility, setVisibility] = useState<GuideVisibility>("members");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    guidesApi
      .list()
      .then(setGuides)
      .catch(() => {
        setGuides([]);
        push("danger", "Impossible de charger les guides");
      });
  }, [push]);

  const create = async () => {
    if (busy || !title.trim()) return;
    setBusy(true);
    try {
      const g = await guidesApi.create({ title: title.trim(), visibility });
      push("success", "Guide créé", "Ajoutez maintenant ses étapes.");
      setOpen(false);
      navigate(`/guides/${g.id}/modifier`);
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        push("warning", "Un guide porte déjà ce titre", "Choisissez un titre différent.");
      } else {
        push("danger", "Création impossible");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="page guides-list">
      <PageHeader
        title="Guides"
        subtitle={isAdmin ? "Procédures, tutoriels et modes opératoires, en étapes." : "Procédures et tutoriels publiés."}
        actions={
          isAdmin ? (
            <Button variant="primary" icon={<Icon name="plus" size={16} />} onClick={() => setOpen(true)}>
              Nouveau guide
            </Button>
          ) : undefined
        }
      />

      {guides === null ? <p className="muted">Chargement…</p> : null}

      {guides && guides.length === 0 ? (
        <div className="empty">
          {isAdmin ? "Aucun guide. Créez le premier." : "Aucun guide publié pour l'instant."}
        </div>
      ) : null}

      {guides && guides.length > 0 && isAdmin ? (
        <table className="table">
          <thead>
            <tr>
              <th>Titre</th>
              <th>Visibilité</th>
              <th>Statut</th>
              <th>Version</th>
              <th>Modifié le</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {guides.map((g) => (
              <tr key={g.id}>
                <td>
                  <Link to={`/guides/${g.slug}`} className="guides-list__title">
                    {g.title}
                  </Link>
                  {g.description ? <span className="guides-list__desc">{g.description}</span> : null}
                </td>
                <td>
                  <GuideVisibilityBadge visibility={g.visibility} />
                </td>
                <td>
                  <GuideStatusBadge status={g.status} />
                </td>
                <td>{g.version === 0 ? "—" : `v${g.version}`}</td>
                <td>{formatDateTime(g.updatedAt, { dateStyle: "medium" })}</td>
                <td className="table__actions">
                  <Link to={`/guides/${g.id}/modifier`}>Modifier</Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}

      {guides && guides.length > 0 && !isAdmin ? (
        <div className="guides-list__grid">
          {guides.map((g) => (
            <Link key={g.id} to={`/guides/${g.slug}`} className="card card--interactive guides-list__card">
              <span className="guides-list__card-head">
                <h3 className="card__title">{g.title}</h3>
                <GuideVisibilityBadge visibility={g.visibility} />
              </span>
              {g.description ? <p className="card__subtitle">{g.description}</p> : null}
              <span className="guides-list__card-foot">
                Mis à jour le {formatDateTime(g.updatedAt, { dateStyle: "medium" })}
              </span>
            </Link>
          ))}
        </div>
      ) : null}

      <Modal
        open={open}
        title="Nouveau guide"
        onClose={() => setOpen(false)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Annuler
            </Button>
            <Button variant="primary" onClick={create} disabled={busy || !title.trim()}>
              Créer
            </Button>
          </>
        }
      >
        <div className="form-grid">
          <div className="form-grid__full">
            <Field label="Titre" hint="Le slug de l'adresse est dérivé du titre ; vous pourrez l'ajuster ensuite.">
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") void create();
                }}
                autoFocus
              />
            </Field>
          </div>
          <div className="form-grid__full">
            <Field label="Visibilité" hint={GUIDE_VISIBILITY_HINTS[visibility]}>
              <Select value={visibility} onChange={(e) => setVisibility(e.target.value as GuideVisibility)}>
                {GUIDE_VISIBILITIES.map((v) => (
                  <option key={v} value={v}>
                    {GUIDE_VISIBILITY_LABELS[v]}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
        </div>
      </Modal>
    </div>
  );
}
